import { useEffect, useState, type ReactNode } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useLogto } from '@logto/react';
import {
  claimAutomaticStaffSignIn,
  releaseAutomaticStaffSignIn,
  rememberStaffSignInReturn,
  staffSignInOptions,
} from '@/providers/staffSignIn';
import { useStaffSession } from '@/providers/useStaffSession';

/** How long the automatic sign-in may take to leave the page before the button is shown. */
const REDIRECT_WAIT_LIMIT_MS = 5_000;

interface StaffSessionRequiredProps {
  /** The dashboard session's address, filled in at Google so the second sign-in needs no typing. */
  email?: string;
  /** Rendered only for a signed-in staff member. */
  children: ReactNode;
}

/**
 * The shared gate also admits a Google dashboard session, which the Facade refuses on the MCP access routes
 * (they serve only a verified staff session). So these pages wait for the staff sign-in themselves. Someone
 * with only the dashboard session is sent through it at once; the button is the fallback when that came back
 * without a session.
 */
const StaffSessionRequired = ({ email, children }: StaffSessionRequiredProps) => {
  const staff = useStaffSession();
  const { signIn } = useLogto();
  const { pathname, search, hash } = useLocation();

  const startSignIn = () => {
    rememberStaffSignInReturn(`${pathname}${search}${hash}`);
    void signIn(staffSignInOptions(email));
  };

  const [redirecting, setRedirecting] = useState(false);
  // A sign-in that has not left the page by now is not going to; offer the button instead of a spinner.
  useEffect(() => {
    if (!redirecting) {
      return;
    }
    const timer = setTimeout(() => setRedirecting(false), REDIRECT_WAIT_LIMIT_MS);
    return () => clearTimeout(timer);
  }, [redirecting]);
  useEffect(() => {
    if (staff.status === 'staff') {
      releaseAutomaticStaffSignIn();
    }
    if (staff.status === 'anonymous' && claimAutomaticStaffSignIn()) {
      setRedirecting(true);
      startSignIn();
    }
    // Runs per status change only; startSignIn reads values that do not change while the page is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff.status]);

  if (staff.status === 'staff') {
    return <>{children}</>;
  }
  if (staff.status === 'loading' || redirecting) {
    return <Spinner animation="border" size="sm" />;
  }
  if (staff.status === 'refused') {
    return (
      <Alert variant="warning">
        You are signed in, but not a member of the staff organization. Ask an administrator to add you.
      </Alert>
    );
  }
  return (
    <Alert variant="info">
      <p>MCP keys are issued only to a signed-in SkinSeoul account.</p>
      <Button onClick={startSignIn}>Sign in with your SkinSeoul account</Button>
    </Alert>
  );
};

export default StaffSessionRequired;
